import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X, Search, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { useZoneSearch, type Zone } from "../../hooks/useZoneSearch";
import type { OperatorWithCoords } from "../../types/database";

export interface ZoneAutocompleteProps {
  value?: string | null;
  onChange: (zone: Zone | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isLoadingOperators?: boolean; // Show loading indicator when operators are being loaded on the map
  filteredCount?: number; // Filtered count for selected zone (with current filters applied)
  filteredOperators?: OperatorWithCoords[]; // All filtered operators (with category/type filters applied) to calculate counts for each zone
}

export function ZoneAutocomplete({
  value,
  onChange,
  placeholder = "Cerca città o regioni...",
  disabled = false,
  className,
  isLoadingOperators = false,
  filteredCount,
  filteredOperators = [],
}: ZoneAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRef = useRef(false); // Track when we're selecting a result

  // Debounce search input - wait 300ms after user stops typing
  useEffect(() => {
    // Skip debounce if we're selecting a result (prevents unnecessary search_zones call)
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    // Cleanup on unmount or when search changes
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [search]);

  // Search zones with debounced value
  // isLoading will be false if placeholderData (matching regions) is available
  const { data: zones = [], isFetching: isFetchingZones } = useZoneSearch(debouncedSearch, 20);

  // Show loader only if we're fetching AND we don't have any zones yet (no placeholder data)
  const showLoader = isFetchingZones && zones.length === 0;

  // Calculate filtered count for a zone based on filteredOperators
  const getFilteredCountForZone = useMemo(() => {
    if (!filteredOperators || filteredOperators.length === 0) {
      return () => undefined;
    }

    // Create a cache of counts per zone
    const countsCache = new Map<string, number>();

    return (zone: Zone): number | undefined => {
      const cacheKey = `${zone.type}:${zone.name.toLowerCase()}`;

      if (countsCache.has(cacheKey)) {
        return countsCache.get(cacheKey);
      }

      let count = 0;
      const zoneNameLower = zone.name.toLowerCase();

      if (zone.type === "city") {
        // Count operators with matching city, or DMO with city in name/address
        count = filteredOperators.filter((op) => {
          const city = op.city?.toLowerCase() || "";
          const name = op.name?.toLowerCase() || "";
          const address = op.address?.toLowerCase() || "";
          const region = op.region?.toLowerCase() || "";

          // Exact city match
          if (city === zoneNameLower) {
            return true;
          }

          // DMO with city in name/address but empty city field
          const isDMO = op.macro_category === 'DMO' ||
            (op.typology && ['DMO', 'APT', 'ATL', 'OGD', 'DMC', 'Consorzio', 'Convention Bureau'].includes(op.typology));

          if (isDMO && (!city || city === '') && zone.region) {
            const zoneRegion = zone.region.toLowerCase();
            if (region === zoneRegion && (name.includes(zoneNameLower) || address.includes(zoneNameLower))) {
              return true;
            }
          }

          return false;
        }).length;
      } else if (zone.type === "region") {
        // Count operators with matching region
        count = filteredOperators.filter((op) => {
          const region = op.region?.toLowerCase() || "";
          return region === zoneNameLower;
        }).length;
      } else {
        // For other types, use total count
        return zone.count;
      }

      countsCache.set(cacheKey, count);
      return count;
    };
  }, [filteredOperators]);

  // Convert zones to options
  const options = useMemo(() => {
    return zones.map((zone) => {
      let typeLabel = "Città";
      if (zone.type === "region") typeLabel = "Regione";
      else if (zone.type === "tourist_area") typeLabel = "Zona Turistica";
      else if (zone.type === "municipality") typeLabel = "Comune";
      else if (zone.type === "operator") typeLabel = "Operatore";

      // Get display count: prefer filtered count if available, otherwise use total count
      const isSelectedZone = value && zone.name.toLowerCase() === value.toLowerCase();
      const filteredCountForZone = getFilteredCountForZone(zone);
      const displayCount = isSelectedZone && filteredCount !== undefined
        ? filteredCount
        : filteredCountForZone !== undefined
          ? filteredCountForZone
          : zone.count;

      return {
        value: zone.name,
        label: `${zone.name} (${typeLabel}${displayCount !== undefined ? `, ${displayCount.toLocaleString("it-IT")}` : ""})`,
        zone,
      };
    });
  }, [zones, filteredCount, value, getFilteredCountForZone]);

  // Use case-insensitive matching for selected option to handle name variations
  const selectedOption = value ? options.find((opt) => opt.value.toLowerCase() === value.toLowerCase()) : undefined;

  // Get display count: use filteredCount if available (for selected zone with filters), otherwise use filtered count for zone or total count
  const getDisplayCount = (zone: Zone): number | undefined => {
    // If this is the selected zone and we have filteredCount, use that (reflects current filters)
    const isSelectedZone = value && zone.name.toLowerCase() === value.toLowerCase();
    if (isSelectedZone && filteredCount !== undefined) {
      return filteredCount;
    }
    // Otherwise use filtered count for zone if available, or total count
    const filteredCountForZone = getFilteredCountForZone(zone);
    return filteredCountForZone !== undefined ? filteredCountForZone : zone.count;
  };

  // Sync search with selected value when value changes externally
  useEffect(() => {
    if (value && !isOpen) {
      // If we have a value, try to find it in options or use it directly
      if (selectedOption) {
        setSearch(selectedOption.zone.name);
      } else if (typeof value === 'string') {
        // Value is a string (zone name), use it directly
        setSearch(value);
      }
    } else if (!value && !isOpen) {
      setSearch("");
    }
  }, [value, selectedOption, isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Keep the selected value visible when closing
        if (value && selectedOption) {
          setSearch(selectedOption.zone.name);
        } else if (!value) {
          setSearch("");
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, selectedOption]);

  const handleSelect = (zoneName: string) => {
    // Use case-insensitive matching to handle variations in capitalization
    const zone = zones.find((z) => z.name.toLowerCase() === zoneName.toLowerCase());

    // Mark that we're selecting a result to prevent debounce from triggering search
    isSelectingRef.current = true;

    // Close dropdown first
    setIsOpen(false);

    // Clear any pending debounce timeout to prevent search_zones call
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Update search immediately with the selected zone name
    // The debounce useEffect will skip updating debouncedSearch because isSelectingRef is true
    setSearch(zone?.name || zoneName);

    // Notify parent component of selection
    onChange(zone || null);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearch("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (isOpen && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex].value);
        } else {
          setIsOpen(true);
        }
        break;
      case "Escape":
        setIsOpen(false);
        // Keep the selected value visible when closing
        if (value && selectedOption) {
          setSearch(selectedOption.zone.name);
        } else if (!value) {
          setSearch("");
        }
        break;
      case "Tab":
        setIsOpen(false);
        // Keep the selected value visible when closing
        if (value && selectedOption) {
          setSearch(selectedOption.zone.name);
        } else if (!value) {
          setSearch("");
        }
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = e.target.value;
    setSearch(newSearch);
    setHighlightedIndex(0);
    if (!isOpen) setIsOpen(true);
    // Clear selection if user types something different from the selected value
    if (value && newSearch !== value && newSearch !== selectedOption?.zone.name) {
      onChange(null);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div
        className={cn(
          "flex items-center h-9 w-full rounded-lg border overlay-solid shadow-lg text-sm transition-all duration-200",
          "focus-within:border-amber-500",
          value ? "border-amber-500" : "border-border",
          disabled && "opacity-50 cursor-not-allowed",
          "pl-3"
        )}
      >
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={
            isOpen
              ? search
              : value
                ? selectedOption
                  ? `${selectedOption.zone.name} (${selectedOption.zone.type === "city" ? "Città" :
                    selectedOption.zone.type === "region" ? "Regione" :
                      selectedOption.zone.type === "tourist_area" ? "Zona Turistica" :
                        selectedOption.zone.type === "municipality" ? "Comune" :
                          selectedOption.zone.type === "operator" ? "Operatore" : "Zona"
                  }${getDisplayCount(selectedOption.zone) !== undefined ? `, ${getDisplayCount(selectedOption.zone)!.toLocaleString("it-IT")}` : ""})`
                  : value
                : ""
          }
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none min-w-0 pr-1 ml-1.5"
        />
        <div className="flex items-center gap-0.5 pr-1.5">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          {(showLoader || isLoadingOperators) && (
            <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
          )}
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={listRef}
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "absolute z-50 mt-1 w-full max-h-[70vh] overflow-y-auto overflow-x-visible rounded-lg",
              "bg-card border border-border",
              "no-scrollbar"
            )}
          >
            {showLoader ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Ricerca in corso...</span>
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {search.length < 2
                  ? "Digita almeno 2 caratteri per cercare"
                  : "Nessun risultato"}
              </div>
            ) : (
              options.map((option, index) => (
                <button
                  key={`${option.zone.type}-${option.value}`}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  title={option.label}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm flex items-center justify-between",
                    "transition-colors duration-100 group/option",
                    "group-hover/option:overflow-visible group-hover/option:relative group-hover/option:z-10",
                    index === highlightedIndex && "overlay-solid-secondary",
                    option.value === value && "text-amber-500 font-medium"
                  )}
                >
                  <span className="flex items-center gap-2 min-w-0 flex-1 group-hover/option:min-w-fit group-hover/option:overflow-visible">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground shrink-0">
                      {option.zone.type === "city" ? "Città" :
                        option.zone.type === "region" ? "Regione" :
                          option.zone.type === "tourist_area" ? "Zona Turistica" :
                            option.zone.type === "municipality" ? "Comune" :
                              option.zone.type === "operator" ? "Operatore" : "Zona"}
                    </span>
                    <span className="truncate group-hover/option:whitespace-nowrap group-hover/option:truncate-none group-hover/option:overflow-visible flex-1">
                      {option.zone.name}
                      {getDisplayCount(option.zone) !== undefined && (
                        <span className="text-muted-foreground ml-1">
                          ({getDisplayCount(option.zone)!.toLocaleString("it-IT")})
                        </span>
                      )}
                    </span>
                  </span>
                  {option.value === value && (
                    <span className="text-amber-500 text-xs">✓</span>
                  )}
                </button>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

